import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon } from '../primitives/Icon.tsx';
import './controls.css';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  /** Names the group for a screen reader, e.g. "Date range". */
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  tone?: 'ink' | 'accent';
}

/**
 * Today / 7 days / 30 days, and Traffic / Sales.
 *
 * Radio inputs under the styling, not buttons: arrow keys move between options, the
 * group is announced as a group, and the selected one is announced as selected. The
 * prototype used clickable divs, where none of that is true.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  tone = 'ink',
}: SegmentedControlProps<T>) {
  const name = useId();

  return (
    <div className="segmented" role="radiogroup" aria-label={label} data-tone={tone}>
      {options.map((option) => {
        const id = `${name}-${option.value}`;
        return (
          <span key={option.value} className="segmentedItem">
            <input
              id={id}
              className="segmentedInput"
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <label className="segmentedLabel" htmlFor={id}>
              {option.label}
            </label>
          </span>
        );
      })}
    </div>
  );
}

export interface DateRangePickerOption<T extends string> {
  value: T;
  label: string;
}

export interface DateRangePickerProps<T extends string> {
  value: T;
  options: DateRangePickerOption<T>[];
  onChange: (value: T) => void;
  customValue: T;
  /** Overrides the trigger label, usually with a formatted custom range. */
  label?: string;
  from: string;
  to: string;
  onRangeChange: (range: { from: string; to: string }) => void;
  /** Today, so the inputs cannot ask for the future. */
  max?: string;
}

/**
 * A reporting range trigger and dark popover.
 *
 * Native date inputs remain inside the popover: they are keyboard-accessible, localised
 * and understood by assistive technology without introducing a bespoke calendar.
 */
export function DateRangePicker<T extends string>({
  value,
  options,
  onChange,
  customValue,
  label,
  from,
  to,
  onRangeChange,
  max,
}: DateRangePickerProps<T>) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const selectRange = (nextValue: T) => {
    onChange(nextValue);
    if (nextValue !== customValue) setOpen(false);
  };

  return (
    <div className="dateRangePicker">
      <button
        ref={triggerRef}
        type="button"
        className="dateRangePickerTrigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={`${id}-popover`}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="calendar" size={16} />
        <span className="dateRangePickerTriggerLabel">{label ?? selectedOption?.label}</span>
        <span className="dateRangePickerTriggerIcon" aria-hidden="true">
          <Icon name="chevron-down" size={14} />
        </span>
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="dateRangePickerBackdrop"
            aria-label="Close date range picker"
            onClick={() => setOpen(false)}
          />
          <div
            id={`${id}-popover`}
            className="dateRangePickerPopover"
            role="dialog"
            aria-label="Date range"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="dateRangePickerOption"
                data-selected={value === option.value}
                onClick={() => selectRange(option.value)}
              >
                {option.label}
              </button>
            ))}
            <div className="dateRangePickerFields">
              <input
                id={`${id}-from`}
                className="dateRangePickerInput"
                aria-label="From date"
                type="date"
                value={from}
                max={to || max}
                onChange={(event) => {
                  onChange(customValue);
                  onRangeChange({ from: event.currentTarget.value, to });
                }}
              />
              <input
                id={`${id}-to`}
                className="dateRangePickerInput"
                aria-label="To date"
                type="date"
                value={to}
                min={from}
                {...(max ? { max } : {})}
                onChange={(event) => {
                  onChange(customValue);
                  onRangeChange({ from, to: event.currentTarget.value });
                }}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export interface FilterChipProps {
  /** What is being filtered to: a partner name, a link label, a discount code. */
  label: string;
  onClear?: () => void;
  style?: CSSProperties;
}

/** Cyan pill showing the active report filter. */
export function FilterChip({ label, onClear, style }: FilterChipProps) {
  return (
    <span className="filterChip" style={style}>
      <span className="filterChipLabel">{label}</span>
      {onClear ? (
        <button
          type="button"
          className="filterChipClear"
          title="Clear filter"
          aria-label="Clear filter"
          onClick={onClear}
        >
          <Icon name="close" size={14} />
        </button>
      ) : null}
    </span>
  );
}

export interface EmptyStateProps {
  title: string;
  body: string;
  action?: ReactNode;
}

/**
 * Nothing here, and why.
 *
 * "No results" alone leaves someone unsure whether the app is broken, the filter is
 * too narrow, or there genuinely were no sales — so every empty state says which.
 */
export function EmptyState({ title, body, action }: EmptyStateProps) {
  return (
    <div className="emptyState">
      <p className="typeCardTitle">{title}</p>
      <p className="typeBody">{body}</p>
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  title: string;
  detail?: string;
  onRetry?: () => void;
}

export function ErrorState({ title, detail, onRetry }: ErrorStateProps) {
  return (
    <div className="errorState" role="alert">
      <p className="typeCardTitle">{title}</p>
      {detail ? <p className="typeBody">{detail}</p> : null}
      {onRetry ? (
        <button type="button" className="btn btnSecondary btnSmall" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}
